import debug from 'debug'
import { OnerwayPaymentMethod } from 'pixai-contract/payment/shared'
import { type FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAsync } from 'react-use'
import MdiLoading from '~icons/mdi/loading'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import type {
  OpenPaymentDialogReturns,
  PaymentExecuteFn,
} from 'src/services/helpers/payment'
import { PaymentProvider } from 'src/services/helpers/payment'
import { getApiEnv } from 'src/utils/config'
import { genRandomString } from 'src/utils/genId'
import Pacypay from '../../../../vendor/onerway/onerway'
import { buildOnerwayError, isOnerwayPaymentError } from './onerwayErrorUtils'
import {
  getOnerwayPaymentData,
  getOnerwayPaymentMessage,
  getOnerwayPaymentStatus,
  getOnerwayPaymentStatusCode,
  getOnerwayResponseCode,
  isOnerwayJcbAeUnionPayRequired,
} from './onerwayResult'
import { reportOnerwayPaymentFailureToSentry } from './onerwaySentry'

const log = debug('pixai:payment:onerway')

type OnerwayCardSubProductType = 'DIRECT' | 'SUBSCRIBE' | 'TOKEN'

function resetOnerwayContainer(containerId: string) {
  // Pacypay owns iframe DOM and has no destroy API. Clear its container
  // before switching card variants so stale transaction state cannot survive.
  const container = document.getElementById(containerId)
  if (!container) return

  while (container.firstChild) {
    container.removeChild(container.firstChild)
  }
  container.style.setProperty('height', 'auto')
  container.style.setProperty('width', 'auto')
}

function ensureOnerwayLocale(code: string): string | undefined {
  // en zh-cn ar de es fi fr it ja ko nl no pl pt ru sv th zh-tw
  const ONERWAY_SUPPORTED_LOCALES = [
    'en',
    'zh-cn',
    'ar',
    'de',
    'es',
    'fi',
    'fr',
    'it',
    'ja',
    'ko',
    'nl',
    'no',
    'pl',
    'pt',
    'ru',
    'sv',
    'th',
    'zh-tw',
  ]
  if (ONERWAY_SUPPORTED_LOCALES.includes(code)) {
    return code
  }
  // attempt to map the language code to the supported locales
  const lang = code.split('-')[0]
  if (ONERWAY_SUPPORTED_LOCALES.includes(lang)) {
    return lang
  }
  return undefined
}

function toOnerwayCardSubProductType(
  value: string | undefined,
): OnerwayCardSubProductType {
  if (value === undefined) {
    throw new Error('Onerway card subProductType not found')
  }
  if (value === 'DIRECT' || value === 'SUBSCRIBE' || value === 'TOKEN') {
    return value
  }
  throw new Error(`Unsupported Onerway card subProductType: ${value}`)
}

function buildDebugOnerwayJcbAeUnionPayRequiredResponse() {
  // Mirrors the Onerway 12003 SDK callback so testers can exercise the
  // recoverable card-tab guidance without using a real unsupported card.
  return {
    respCode: '20000',
    respMsg: 'Success',
    data: {
      status: 'F',
      statusCode: '12003',
      respMsg: 'Unsupported card brand',
    },
  }
}

interface PaymentDialogContentOnerwayProps {
  execute: PaymentExecuteFn
  executeProvider?:
    | PaymentProvider.onerway
    | PaymentProvider.onerwayJcbAeUnionPay
  trackingFnAfterFetch?: () => void
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
  onJcbAeUnionPayRequired?: () => void
}

export const PaymentDialogContentOnerway: FC<
  PaymentDialogContentOnerwayProps
> = ({
  execute,
  executeProvider = PaymentProvider.onerway,
  trackingFnAfterFetch,
  onSucceeded,
  onFailed,
  onJcbAeUnionPayRequired,
}) => {
  const [paymentConfig, setPaymentConfig] = useState<{
    transactionId: string
    orderId: string
    subProductType: OnerwayCardSubProductType
  } | null>(null)
  const { t, i18n } = useTranslation(['membership'])
  const containerId = useRef('onerway_' + genRandomString(16)).current
  const [threeDSecureUrl, setThreeDSecureUrl] = useState<string | null>(null)

  const { error } = useAsync(async () => {
    const result =
      executeProvider === PaymentProvider.onerwayJcbAeUnionPay
        ? await execute({
            provider: PaymentProvider.onerwayJcbAeUnionPay,
            providerOptions: { paymentMethod: OnerwayPaymentMethod.CARD },
          })
        : await execute({ provider: PaymentProvider.onerway })
    setPaymentConfig({
      transactionId: result.transactionId,
      orderId: result.orderId,
      subProductType: toOnerwayCardSubProductType(result.subProductType),
    })
    trackingFnAfterFetch?.()
  })

  useEffect(() => {
    if (error) {
      log('[Payment:Onerway] Failed to fetch transaction ID', error)
      onFailed?.(error)
    }
  }, [error])

  const handleOnerwayPaymentCompleted = (res: unknown) => {
    if (!paymentConfig) return

    log('[Payment:Onerway] Payment completed with response', res)
    const txtInfo = getOnerwayPaymentData(res) as
      | { redirectUrl?: string }
      | undefined
    const respCode = getOnerwayResponseCode(res)
    const paymentStatus = getOnerwayPaymentStatus(res)
    const paymentStatusCode = getOnerwayPaymentStatusCode(res)
    const paymentMessage = getOnerwayPaymentMessage(res)
    if (
      executeProvider === PaymentProvider.onerway &&
      isOnerwayJcbAeUnionPayRequired(res)
    ) {
      // Some cards cannot be used for Onerway subscription payments in the
      // default card flow. Treat 12003 as recoverable and guide the user to
      // recreate the order from the JCB/Amex/UnionPay tab.
      reportOnerwayPaymentFailureToSentry({
        executeProvider,
        source: 'card-group-mismatch',
        orderId: paymentConfig.orderId,
        transactionId: paymentConfig.transactionId,
        response: res,
        fallbackRespCode: paymentStatusCode ?? respCode,
        fallbackRespMsg: paymentMessage,
      })
      onJcbAeUnionPayRequired?.()
      return
    }

    if (respCode === '20000') {
      switch (paymentStatus) {
        case 'S': // succeeded
          onSucceeded?.({
            finalizedPaymentProvider: executeProvider,
            orderId: paymentConfig.orderId,
          })
          break
        case 'R': // requires 3ds verification
          if (txtInfo?.redirectUrl) {
            window.location.href = txtInfo.redirectUrl
            setThreeDSecureUrl(txtInfo.redirectUrl)
            resetOnerwayContainer(containerId)
          } else {
            toast.error('Redirect URL not found')
          }
          break
        case 'F': // failed
          console.error('[Payment:Onerway] Payment failed', res)
          reportOnerwayPaymentFailureToSentry({
            executeProvider,
            source: 'payment-status-failed',
            orderId: paymentConfig.orderId,
            transactionId: paymentConfig.transactionId,
            response: res,
            fallbackRespCode: paymentStatusCode ?? respCode,
            fallbackRespMsg: paymentMessage,
          })
          toast.error(t('error:codes.payment-failed'))
          onFailed?.(
            buildOnerwayError({
              respCode: paymentStatusCode ?? respCode,
              respMsg: paymentMessage,
              data: txtInfo,
            }),
          )
          break
      }
    } else {
      console.error('[Payment:Onerway] Payment error', res)
      reportOnerwayPaymentFailureToSentry({
        executeProvider,
        source: 'payment-response-error',
        orderId: paymentConfig.orderId,
        transactionId: paymentConfig.transactionId,
        response: res,
        fallbackRespCode: paymentStatusCode ?? respCode,
        fallbackRespMsg: paymentMessage,
      })
      toast.error(t('error:codes.payment-failed'))
      onFailed?.(
        buildOnerwayError({
          respCode: paymentStatusCode ?? respCode,
          respMsg: paymentMessage,
          data: txtInfo,
        }),
      )
    }
  }

  useLayoutEffect(() => {
    if (!paymentConfig) return

    // We must wait until the dialog is mounted before initializing the Onerway transaction
    const instance = new Pacypay(paymentConfig.transactionId, {
      container: containerId,
      locale: ensureOnerwayLocale(i18n.language) ?? 'en',
      environment:
        getApiEnv().isProd || getApiEnv().isStaging ? 'production' : 'sandbox',
      mode: 'CARD',
      config: {
        subProductType: paymentConfig.subProductType,
        checkoutTheme: 'light',
        buttonSeparation: false,
        displayBillingInformation: true,
        variables: {
          colorBackground: '#f5f5f5',
          colorPrimary: '#999999',
          colorText: '#000000',
          colorDanger: '#dc2626',
          borderRadius: '4px',
          fontSizeBase: '14px',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        },
        styles: {
          body: {
            'margin-top': '10px',
            background: '#f5f5f5',
          },
          '.pacypay-checkout__payment-method__image': {
            'object-fit': 'contain',
            filter: 'grayscale(1)',
            '-webkit-user-drag': 'none',
            '-webkit-user-select': 'none',
            '-moz-user-select': 'none',
            '-ms-user-select': 'none',
            'user-select': 'none',
          },
          '.pacypay-checkout__payment-method__header': {
            padding: '10px 14px 10px 3px',
            'flex-wrap': 'nowrap',
            // display: 'none',
          },
          '.pacypay-checkout__payment-method': {
            'border-color': 'transparent', // i dont know why but setting to none will interfere with their height calculation.......
            'border-radius': '0',
          },
          '.pacypay-checkout__input': {
            background: '#EDEDED',
          },
          '.pacypay-checkout__btn-wrap': {
            margin: '0 10px 10px 10px !important',
            transition: 'all 0.2s ease-out',
          },
          '.pacypay-checkout__btn-wrap:hover': {
            filter: 'brightness(1.15)',
          },
          '.pacypay-checkout__btn-wrap:active': {
            filter: 'brightness(0.8)',
            transform: 'scale(0.975)',
          },
          '.pacypay-checkout__error-text': {
            'font-family': 'var(--fontFamily)',
            padding: '2px',
          },
          '.pacypay-checkout__payment-method__details': {
            margin: '10px 0 0 0',
          },
          '.pacypay-checkout__payment-method__name': {
            'font-variant-numeric': 'tabular-nums',
          },
          '.pacypay-checkout__modal-wrap': {
            'flex-direction': 'row',
            'font-family': 'var(--fontFamily)',
          },
          '.pacypay-checkout__modal': {
            overflow: 'hidden',
            'border-radius': '8px',
            'min-height': 'auto',
          },
          '.pacypay-checkout__modal__close': {
            display: 'none',
          },
          '.pacypay-checkout__modal__title': {
            padding: '10px 15px',
          },
          '.pacypay-checkout__modal__buttons': {
            'background-color': 'transparent',
            'margin-top': '0px',
            'margin-bottom': '0px',
            'padding-left': '15px',
            display: 'flex',
          },
          '.pacypay-checkout__modal__content': {
            padding: '10px 15px',
          },
        },
      },
      onPaymentCompleted: res => {
        handleOnerwayPaymentCompleted(res)
      },
      onError: err => {
        console.error('[Payment:Onerway] Payment error', err)
        toast.error(t('error:codes.payment-failed'))
        if (isOnerwayPaymentError(err)) {
          const onerwayError = buildOnerwayError({
            respCode: err.respCode,
            respMsg: err.respMsg,
            data: (err as any)?.data,
          })
          reportOnerwayPaymentFailureToSentry({
            executeProvider,
            source: 'sdk-error',
            orderId: paymentConfig.orderId,
            transactionId: paymentConfig.transactionId,
            response: err,
            error: onerwayError,
            fallbackRespCode: err.respCode,
            fallbackRespMsg: err.respMsg,
          })
          onFailed?.(onerwayError)
          return
        }
        const unknownError = new Error(
          'Unknown Error occurred during payment processing.',
        )
        reportOnerwayPaymentFailureToSentry({
          executeProvider,
          source: 'sdk-error',
          orderId: paymentConfig.orderId,
          transactionId: paymentConfig.transactionId,
          error: err instanceof Error ? err : unknownError,
          fallbackRespMsg:
            err instanceof Error ? err.message : unknownError.message,
        })
        onFailed?.(unknownError)
      },
      onChangeWH: o => {
        const { width, height } = JSON.parse(o)
        document
          .getElementById(containerId)
          ?.style.setProperty('height', `${height + 20}px`)
        document
          .getElementById(containerId)
          ?.style.setProperty('width', `${width}px`)
      },
    })

    // dirty hack to remove `title` from the `iframe` pacypay creates.
    // That `title` is both annoying and makes the iframe 100000% less trustworthy
    const timer = setInterval(() => {
      const iframe = document
        .getElementById(containerId)
        ?.querySelector('iframe')
      if (iframe) {
        iframe.title = ''
        clearInterval(timer)
      }
    }, 1000)

    log('[Payment:Onerway] Initialized Onerway instance', instance)

    return () => {
      clearInterval(timer)
      resetOnerwayContainer(containerId)
    }
  }, [paymentConfig])

  const showDebug12003Button =
    paymentConfig &&
    executeProvider === PaymentProvider.onerway &&
    !getApiEnv().isProd &&
    !getApiEnv().isStaging

  if (threeDSecureUrl) {
    return (
      <div className="w-full rounded-lg flex flex-col items-center duration-300 justify-center text-black/75 text-center bg-white select-none px-4 py-8 gap-4">
        <div className="w-full flex flex-col items-center justify-center gap-2 py-2">
          <MdiLoading className="size-6 animate-spin" />

          <div className="opacity-70 text-sm mt-2">
            <Trans
              i18nKey="membership:payment.threedsecure.title"
              components={[
                <a
                  href={threeDSecureUrl}
                  rel="noreferrer noopener"
                  className="text-blue-500 hover:underline"
                />,
              ]}
            />
          </div>
        </div>

        <div className="h-px w-full bg-black/10" />

        <div className="opacity-70 text-left w-full [&>p:not(:last-child)]:mb-2 text-xs">
          <MarkdownRenderer>
            {t('membership:payment.threedsecure.faq')}
          </MarkdownRenderer>
        </div>
      </div>
    )
  }

  return paymentConfig ? (
    <div className="grid gap-2">
      {showDebug12003Button && (
        <button
          className="justify-self-start rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-700 transition hover:bg-amber-500/20 active:brightness-95"
          onClick={() => {
            handleOnerwayPaymentCompleted(
              buildDebugOnerwayJcbAeUnionPayRequiredResponse(),
            )
          }}
          type="button"
        >
          Simulate 12003
        </button>
      )}
      <div
        // h-[100px] is just for setting an intrinsic height for the iframe initially
        className="h-[100px] max-w-full bg-neutral-100 rounded-lg overflow-y-auto flex items-center duration-300"
        id={containerId}
      />
    </div>
  ) : (
    <div className="h-[100px] w-full rounded-lg flex items-center duration-300 justify-center text-black/75 text-center text-sm bg-neutral-100 select-none">
      <MdiLoading className="size-6 animate-spin" />
    </div>
  )
}
